import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
    Anchor, ArrowLeft, Users, DollarSign, Sparkles, LifeBuoy, Loader2,
    Search, ChevronLeft, ChevronRight, ShieldAlert, MessageSquare, Trash2,
    TrendingUp, Receipt, Filter, CornerDownRight, Send, Check,
    SlidersHorizontal, Lock,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isNativeApp } from "@/lib/platform";

/** Admin console — owner-only, web-only. Backend gate: /api/admin/* (403 otherwise). */
export default function AdminDashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [denied, setDenied] = useState(false);

    useEffect(() => {
        api.get("/admin/stats")
            .then(({ data }) => setStats(data))
            .catch((e) => { if (e.response?.status === 403) setDenied(true); });
    }, []);

    if (isNativeApp()) return <Navigate to="/app" replace />;

    if (denied) {
        return (
            <div data-testid="admin-denied" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, color: "var(--bm-text-muted)" }}>
                <ShieldAlert size={32} color="var(--bm-gold)" />
                <div>This area is reserved for the ship's owner.</div>
                <button className="bm-btn bm-btn-ghost" onClick={() => nav("/app")} data-testid="admin-denied-back-btn">Back to the helm</button>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" style={{ minHeight: "100vh", background: "var(--bm-bg-0)", paddingBottom: 80 }}>
            <div className="bm-safe-top" style={{ borderBottom: "1px solid var(--bm-border-soft)", background: "var(--bm-bg-1)" }}>
                <div style={{ maxWidth: 1100, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16 }}>
                    <button data-testid="admin-back-btn" type="button" className="bm-btn bm-btn-ghost" onClick={() => nav("/app")} style={{ padding: "6px 10px" }}>
                        <ArrowLeft size={16} /> Back
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Anchor size={20} color="var(--bm-gold)" />
                        <h1 className="bm-display" style={{ margin: 0, fontSize: 18 }}>Admiral's Office</h1>
                    </div>
                </div>
            </div>

            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 24px" }}>
                {!stats ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
                        <Loader2 size={24} className="bm-spin" color="var(--bm-gold)" />
                    </div>
                ) : (
                    <>
                        <StatCards stats={stats} />
                        <TrendCard trend={stats.signup_trend} />
                        <FunnelCard funnel={stats.funnel} />
                        <RevenueDashboard stats={stats} />
                        <BreakdownAndRefs stats={stats} />
                    </>
                )}
                <PaywallHits />
                <ReleaseSwitches />
                <UsersSection />
                <FeedbackSection />
            </div>
        </div>
    );
}

const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso) => (iso || "").slice(0, 10);

function StatCard({ icon, label, value, sub, testid }) {
    return (
        <div className="bm-card" data-testid={testid} style={{ padding: "18px 20px", flex: "1 1 180px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)" }}>
                {icon} {label}
            </div>
            <div className="bm-serif" style={{ fontSize: 30, color: "var(--bm-gold)", marginTop: 8 }}>{value}</div>
            {sub && <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

function StatCards({ stats }) {
    const s = stats.subscriptions;
    const r = stats.revenue;
    const paying = s.monthly_active + s.annual_active;
    return (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
            <StatCard icon={<Users size={13} />} label="Total users" value={stats.total_users}
                sub={`+${stats.signups_7d} this week · +${stats.signups_30d} this month`} testid="admin-stat-users" />
            <StatCard icon={<DollarSign size={13} />} label="MRR (est.)" value={money(r.mrr_cents)}
                sub={`≈ ${money(r.arr_cents)} / year`} testid="admin-stat-mrr" />
            <StatCard icon={<Sparkles size={13} />} label="Paying subscribers" value={paying}
                sub={`${s.monthly_active} monthly · ${s.annual_active} annual`} testid="admin-stat-paying" />
            <StatCard icon={<Anchor size={13} />} label="Founders sold" value={`${s.lifetime_paid} / 150`}
                sub={`${money(r.lifetime_revenue_cents)} lifetime revenue`} testid="admin-stat-founders" />
            <StatCard icon={<LifeBuoy size={13} />} label="Free captains" value={s.free_total ?? 0}
                sub={`${s.trial_active} comped Pro · ${s.payment_failed} payment issues`} testid="admin-stat-trials" />
        </div>
    );
}

function TrendCard({ trend }) {
    return (
        <div className="bm-card" data-testid="admin-signup-trend" style={{ padding: "18px 20px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)", marginBottom: 12 }}>
                Signups — last 30 days
            </div>
            <div style={{ width: "100%", height: 160 }}>
                <ResponsiveContainer>
                    <BarChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--bm-text-muted)" }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "var(--bm-text-muted)" }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "var(--bm-panel, #0c1828)", border: "1px solid var(--bm-border)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "var(--bm-text-muted)" }} cursor={{ fill: "rgba(212,181,114,0.08)" }} />
                        <Bar dataKey="signups" fill="var(--bm-gold, #d4b572)" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

/** FunnelCard — the four-step captain funnel: signed up → vessel added →
 * entry logged → paid. Reveals exactly where the drop-off is happening.
 * Rendered as horizontal bars sized proportional to the top-of-funnel count
 * with drop-off deltas in red between rows. Owner accounts excluded on the
 * backend so numbers reflect real captain behaviour.
 */
function FunnelCard({ funnel }) {
    if (!funnel) return null;
    const steps = [
        { key: "signed_up",  label: "Signed up",       tone: "neutral", tip: "New account created" },
        { key: "with_vessel",label: "Added a vessel",  tone: "neutral", tip: "≥1 vessel on the account" },
        { key: "with_entry", label: "Logged an entry", tone: "neutral", tip: "≥1 entry (fuel / maintenance / etc.)" },
        { key: "paid",       label: "Paid",            tone: "gold",    tip: "Active subscription or Lifetime" },
    ];
    const top = Math.max(1, funnel.signed_up || 0);
    return (
        <div className="bm-card" data-testid="admin-funnel-card" style={{ padding: "18px 20px", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)", marginBottom: 14 }}>
                <Filter size={13} /> Captain funnel — where they drop off
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {steps.map((s, idx) => {
                    const val = funnel[s.key] || 0;
                    const pctOfTop = (val / top) * 100;
                    const prev = idx > 0 ? (funnel[steps[idx - 1].key] || 0) : val;
                    const pctOfPrev = prev > 0 ? (val / prev) * 100 : 100;
                    const dropoff = prev - val;
                    const dropoffPct = prev > 0 ? Math.round((dropoff / prev) * 100) : 0;
                    return (
                        <div key={s.key} data-testid={`admin-funnel-row-${s.key}`}>
                            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                                <div style={{ fontSize: 13, color: "var(--bm-text)" }}>{s.label}</div>
                                <div style={{ fontSize: 13, color: s.tone === "gold" ? "var(--bm-gold)" : "var(--bm-text)", fontVariantNumeric: "tabular-nums" }}>
                                    <span className="bm-serif" style={{ fontSize: 20 }}>{val.toLocaleString()}</span>
                                    <span style={{ fontSize: 11, color: "var(--bm-text-muted)", marginLeft: 8 }}>
                                        {pctOfTop.toFixed(1)}% of top
                                    </span>
                                </div>
                            </div>
                            <div
                                style={{
                                    height: 10, borderRadius: 6,
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid var(--bm-border-soft)",
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                                title={s.tip}
                            >
                                <div
                                    data-testid={`admin-funnel-bar-${s.key}`}
                                    style={{
                                        width: `${pctOfTop}%`,
                                        height: "100%",
                                        background: s.tone === "gold"
                                            ? "linear-gradient(90deg, var(--bm-gold), rgba(212,181,114,0.6))"
                                            : "linear-gradient(90deg, rgba(120,180,220,0.85), rgba(120,180,220,0.35))",
                                        transition: "width 400ms ease",
                                    }}
                                />
                            </div>
                            {idx < steps.length - 1 && (
                                <div
                                    data-testid={`admin-funnel-dropoff-${s.key}`}
                                    style={{
                                        fontSize: 11, color: dropoffPct >= 40 ? "var(--bm-red, #d97a7a)" : "var(--bm-text-muted)",
                                        marginTop: 4, marginLeft: 2, marginBottom: 6,
                                        display: "flex", alignItems: "center", gap: 4,
                                    }}
                                >
                                    <span style={{ opacity: 0.7 }}>↓</span>
                                    <span>
                                        <span style={{ fontVariantNumeric: "tabular-nums" }}>{dropoff.toLocaleString()}</span> drop-off
                                        <span style={{ marginLeft: 6 }}>·</span>
                                        <span style={{ marginLeft: 6 }}>{pctOfPrev.toFixed(1)}% continued</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div style={{ fontSize: 11, color: "var(--bm-text-dim)", marginTop: 12, lineHeight: 1.5 }}>
                Owner accounts are excluded. Big drop-off at "Added a vessel" means the onboarding sign-up flow needs a nudge; drop-off at "Logged an entry" points at activation UX; drop-off at "Paid" is your pricing / paywall.
            </div>
        </div>
    );
}

/** RevenueDashboard — captain's at-a-glance view of trial conversion, 30-day
 * revenue trend, and the 10 most recent paid transactions. All data lives
 * in /api/admin/stats (extended Feb 8, 2026) — no extra round trips.
 */
function RevenueDashboard({ stats }) {
    const conv = stats.conversion || { pct: 0, cohort_total: 0, cohort_converted: 0 };
    const trend = (stats.revenue && stats.revenue.trend_30d) || [];
    const total30 = (stats.revenue && stats.revenue.total_30d_cents) || 0;
    const payments = stats.recent_payments || [];

    return (
        <div data-testid="admin-revenue-dashboard" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
            {/* Free → Paid conversion — single big number, business-health glance */}
            <div className="bm-card" data-testid="admin-conversion-card" style={{ padding: "18px 20px", flex: "1 1 200px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)" }}>
                    <TrendingUp size={13} /> Free → Paid
                </div>
                <div className="bm-serif" style={{ fontSize: 30, color: "var(--bm-gold)", marginTop: 8 }}>
                    {conv.pct.toFixed(1)}%
                </div>
                <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 2 }}>
                    {conv.cohort_converted} of {conv.cohort_total} signups ≥7d old
                </div>
            </div>

            {/* 30-day revenue trend */}
            <div className="bm-card" data-testid="admin-revenue-trend" style={{ padding: "18px 20px", flex: "2 1 420px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)" }}>
                        Revenue — last 30 days
                    </div>
                    <div style={{ fontSize: 13, color: "var(--bm-gold)" }} data-testid="admin-revenue-30d-total">
                        {money(total30)} total
                    </div>
                </div>
                <div style={{ width: "100%", height: 140 }}>
                    <ResponsiveContainer>
                        <BarChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: -8 }}>
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--bm-text-muted)" }} tickFormatter={(d) => d.slice(5)} interval={4} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 10, fill: "var(--bm-text-muted)" }} axisLine={false} tickLine={false} tickFormatter={(c) => `$${(c / 100).toFixed(0)}`} />
                            <Tooltip
                                contentStyle={{ background: "var(--bm-panel, #0c1828)", border: "1px solid var(--bm-border)", borderRadius: 8, fontSize: 12 }}
                                labelStyle={{ color: "var(--bm-text-muted)" }}
                                cursor={{ fill: "rgba(212,181,114,0.08)" }}
                                formatter={(c) => money(c)}
                            />
                            <Bar dataKey="cents" fill="var(--bm-gold, #d4b572)" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Recent paid transactions */}
            <div className="bm-card" data-testid="admin-recent-payments" style={{ padding: "18px 20px", flex: "1 1 320px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)", marginBottom: 12 }}>
                    <Receipt size={13} /> Recent payments
                </div>
                {payments.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>
                        No paid transactions yet — Stripe receipts will appear here.
                    </div>
                ) : payments.map((p, idx) => (
                    <div key={`${p.created_at}-${idx}`} data-testid="admin-payment-row" style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--bm-border-soft)", fontSize: 12, gap: 8 }}>
                        <span style={{ color: "var(--bm-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "1 1 auto", minWidth: 0 }}>
                            {p.email || "—"}
                        </span>
                        <span style={{ color: "var(--bm-text)" }}>
                            <span style={{ color: "var(--bm-text-muted)", fontSize: 11 }}>{p.plan}</span> {money(p.amount_cents || 0)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BreakdownAndRefs({ stats }) {
    const s = stats.subscriptions;
    return (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 26 }}>
            <div className="bm-card" data-testid="admin-billing-breakdown" style={{ padding: "18px 20px", flex: "1 1 300px" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)", marginBottom: 12 }}>
                    Billing breakdown
                </div>
                {[["Stripe (web)", s.stripe_active], ["Apple (iOS)", s.apple_active], ["Lifetime (incl. comps)", s.lifetime_total], ["Expired / churned", s.expired]].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--bm-border-soft)", fontSize: 13 }}>
                        <span style={{ color: "var(--bm-text-muted)" }}>{label}</span>
                        <span style={{ color: "var(--bm-text)" }}>{val}</span>
                    </div>
                ))}
            </div>
            <div className="bm-card" data-testid="admin-refs" style={{ padding: "18px 20px", flex: "1 1 380px" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--bm-text-muted)", marginBottom: 12 }}>
                    Where signups come from
                </div>
                {stats.refs.length === 0 ? (
                    <div style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>No tagged signups yet — QR poster scans + captain referrals via <code>/?ref=&lt;user.id&gt;</code> will appear here.</div>
                ) : (
                    <>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 60px 60px 70px",
                            gap: 8, padding: "6px 0",
                            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                            color: "var(--bm-text-muted)", borderBottom: "1px solid var(--bm-border-soft)",
                        }}>
                            <span>Ref</span>
                            <span style={{ textAlign: "right" }}>Signups</span>
                            <span style={{ textAlign: "right" }}>Paid</span>
                            <span style={{ textAlign: "right" }}>Conv %</span>
                        </div>
                        {stats.refs.map((r) => (
                            <div
                                key={r.ref}
                                data-testid={`admin-ref-row-${r.ref}`}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 60px 60px 70px",
                                    gap: 8, padding: "8px 0",
                                    borderBottom: "1px solid var(--bm-border-soft)",
                                    fontSize: 13, alignItems: "center",
                                }}
                            >
                                <span style={{ color: "var(--bm-gold)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.ref}>
                                    {r.ref}
                                </span>
                                <span style={{ color: "var(--bm-text)", textAlign: "right" }}>{r.signups}</span>
                                <span style={{ color: "var(--bm-text)", textAlign: "right" }}>{r.paid}</span>
                                <span
                                    style={{
                                        color: r.conversion_pct >= 20 ? "var(--bm-good, #4ade80)" : r.conversion_pct >= 5 ? "var(--bm-gold)" : "var(--bm-text-muted)",
                                        textAlign: "right", fontWeight: 500,
                                    }}
                                >
                                    {r.conversion_pct?.toFixed?.(1) ?? "0.0"}%
                                </span>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

function statusBadge(u) {
    const proUntilActive = u.pro_until && new Date(u.pro_until) > new Date();
    const map = {
        lifetime: ["⚓ Lifetime", "var(--bm-gold)"],
        active: [u.subscription_plan === "annual" ? "Annual" : "Monthly", "var(--bm-good, #4ade80)"],
        free: [proUntilActive ? "Free · comped Pro" : "Free", "var(--bm-text)"],
        trial: ["Trial (legacy)", "var(--bm-text-muted)"],
        expired: ["Expired", "var(--bm-bad, #f87171)"],
        payment_failed: ["Payment failed", "var(--bm-bad, #f87171)"],
    };
    const [label, color] = map[u.subscription_status] || [u.subscription_status, "var(--bm-text-muted)"];
    return <span className="bm-tag" style={{ color, borderColor: "var(--bm-border)", fontSize: 11 }}>{label}{u.billing_provider === "apple" ? " · " : ""}{u.billing_provider === "apple" ? "Apple" : ""}</span>;
}

function UsersSection() {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [notice, setNotice] = useState("");

    const load = useCallback(async (query, p) => {
        try {
            const { data } = await api.get("/admin/users", { params: { q: query, page: p } });
            setData(data);
        } catch { /* gate already handled at page level */ }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); load(q, 1); }, 300);
        return () => clearTimeout(t);
    }, [q, load]);

    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    const go = (p) => { setPage(p); setExpanded(null); load(q, p); };

    return (
        <section className="bm-card" data-testid="admin-users-section" style={{ padding: "20px 20px 12px", marginBottom: 26 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>
                    Crew manifest {data && <span style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>· {data.total} real accounts</span>}
                </h2>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ position: "relative" }}>
                        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--bm-text-muted)" }} />
                        <input
                            data-testid="admin-user-search"
                            className="bm-input"
                            placeholder="Search email or name…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            style={{ paddingLeft: 32, width: 240 }}
                        />
                    </div>
                </div>
            </div>

            {notice && (
                <div data-testid="admin-users-notice" style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.3)", color: "var(--bm-good, #4ade80)", fontSize: 13 }}>
                    {notice}
                </div>
            )}

            {!data ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 30 }}><Loader2 size={20} className="bm-spin" /></div>
            ) : (
                <>
                    {data.users.map((u) => (
                        <UserRow key={u.id} user={u} expanded={expanded === u.id}
                            onToggle={() => setExpanded(expanded === u.id ? null : u.id)}
                            onChanged={() => load(q, page)}
                            onDeleted={(message) => { setNotice(message); setExpanded(null); load(q, page); }} />
                    ))}
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, padding: "12px 0 4px", fontSize: 13, color: "var(--bm-text-muted)" }}>
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-users-prev" disabled={page <= 1} onClick={() => go(page - 1)} style={{ padding: "4px 8px" }}><ChevronLeft size={14} /></button>
                        Page {page} of {pages}
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-users-next" disabled={page >= pages} onClick={() => go(page + 1)} style={{ padding: "4px 8px" }}><ChevronRight size={14} /></button>
                    </div>
                </>
            )}
        </section>
    );
}

function UserRow({ user, expanded, onToggle, onChanged, onDeleted }) {
    return (
        <div style={{ borderBottom: "1px solid var(--bm-border-soft)" }}>
            <button
                type="button"
                data-testid={`admin-user-row-${user.email}`}
                onClick={onToggle}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "11px 4px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
            >
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "var(--bm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {user.email} {user.is_owner_account && <span title="Owner account">⚓</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>{user.name}</div>
                </div>
                {statusBadge(user)}
                <span className="bm-mobile-hide" style={{ fontSize: 12, color: "var(--bm-text-muted)", width: 90 }}>{user.vessel_count} vessel{user.vessel_count === 1 ? "" : "s"}</span>
                <span className="bm-mobile-hide" style={{ fontSize: 12, color: "var(--bm-text-muted)", width: 86 }}>{day(user.created_at)}</span>
            </button>
            {expanded && <UserActions user={user} onChanged={onChanged} onDeleted={onDeleted} />}
        </div>
    );
}

function UserActions({ user, onChanged, onDeleted }) {
    const [busy, setBusy] = useState(null);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [confirmDelete, setConfirmDelete] = useState("");

    const run = async (key, fn) => {
        setBusy(key); setMsg(""); setErr("");
        try {
            const out = await fn();
            setMsg(out || "Done.");
            onChanged?.();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
        } finally {
            setBusy(null);
        }
    };

    const extend = (days) => run(`extend${days}`, async () => {
        const { data } = await api.post(`/admin/users/${user.id}/extend-trial`, { days });
        return `Pro comped until ${day(data.pro_until || data.trial_ends_at)}.`;
    });
    const grant = () => run("grant", async () => {
        await api.post(`/admin/users/${user.id}/grant-lifetime`);
        return "Complimentary lifetime granted. ⚓";
    });
    const remove = async () => {
        setBusy("delete"); setErr("");
        try {
            const { data } = await api.delete(`/admin/users/${user.id}`, { data: { confirmation: confirmDelete } });
            // Message is lifted to UsersSection — this row unmounts on refresh.
            onDeleted?.(data.message);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
            setBusy(null);
        }
    };

    const subscriber = user.subscription_status === "active" || user.subscription_status === "lifetime";

    return (
        <div data-testid="admin-user-actions" style={{ padding: "4px 4px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
            {msg && <div data-testid="admin-action-msg" style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.3)", color: "var(--bm-good, #4ade80)", fontSize: 13 }}>{msg}</div>}
            {err && <div data-testid="admin-action-err" style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", color: "var(--bm-bad, #f87171)", fontSize: 13 }}>{err}</div>}
            <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                Joined {day(user.created_at)} · comped Pro until {day(user.pro_until) || "—"} · ref: {user.signup_ref || "—"}{user.comped ? " · comped" : ""}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!subscriber && (
                    <>
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-extend-7-btn" disabled={!!busy} onClick={() => extend(7)} style={{ padding: "6px 12px", fontSize: 13 }}>
                            {busy === "extend7" ? <Loader2 size={13} className="bm-spin" /> : "+7d Pro"}
                        </button>
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-extend-30-btn" disabled={!!busy} onClick={() => extend(30)} style={{ padding: "6px 12px", fontSize: 13 }}>
                            {busy === "extend30" ? <Loader2 size={13} className="bm-spin" /> : "+30d Pro"}
                        </button>
                    </>
                )}
                {user.subscription_status !== "lifetime" && (
                    <button className="bm-btn bm-btn-ghost" data-testid="admin-grant-lifetime-btn" disabled={!!busy} onClick={grant} style={{ padding: "6px 12px", fontSize: 13, color: "var(--bm-gold)", borderColor: "var(--bm-gold-dim)" }}>
                        {busy === "grant" ? <Loader2 size={13} className="bm-spin" /> : "⚓ Grant lifetime"}
                    </button>
                )}
                {!user.is_owner_account && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
                        <input
                            data-testid="admin-delete-confirm-input"
                            className="bm-input"
                            placeholder="Type email to confirm delete"
                            value={confirmDelete}
                            onChange={(e) => setConfirmDelete(e.target.value)}
                            style={{ width: 220, fontSize: 12, padding: "6px 10px" }}
                        />
                        <button
                            className="bm-btn bm-btn-ghost"
                            data-testid="admin-delete-user-btn"
                            disabled={!!busy || confirmDelete.trim().toLowerCase() !== (user.email || "").toLowerCase()}
                            onClick={remove}
                            style={{ padding: "6px 12px", fontSize: 13, color: "var(--bm-bad, #f87171)", borderColor: "rgba(248,113,113,0.3)" }}
                        >
                            {busy === "delete" ? <Loader2 size={13} className="bm-spin" /> : <><Trash2 size={13} /> Delete</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function FeedbackSection() {
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [openReplyFor, setOpenReplyFor] = useState(null); // fb_id currently being replied to

    const load = useCallback(() => {
        api.get("/admin/feedback", { params: { page } })
            .then(({ data }) => setData(data))
            .catch(() => {});
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    // Determine if a captain can be replied to (has an email on file, either
    // typed in the FAB or linked to a signed-in account).
    const canReply = (f) => !!(f.submitted_email || f.user_id);

    return (
        <section className="bm-card" data-testid="admin-feedback-section" style={{ padding: "20px 20px 12px" }}>
            <h2 className="bm-serif" style={{ fontSize: 22, margin: "0 0 14px", fontWeight: 500, display: "flex", alignItems: "center", gap: 10 }}>
                <MessageSquare size={18} color="var(--bm-gold)" /> Feedback inbox {data && <span style={{ fontSize: 13, color: "var(--bm-text-muted)" }}>· {data.total}</span>}
            </h2>
            {!data ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Loader2 size={18} className="bm-spin" /></div>
            ) : data.feedback.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--bm-text-muted)", paddingBottom: 10 }}>No feedback yet — the FAB will deliver it here.</div>
            ) : (
                <>
                    {data.feedback.map((f) => (
                        <FeedbackItem
                            key={f.id}
                            f={f}
                            canReply={canReply(f)}
                            isOpen={openReplyFor === f.id}
                            onOpenReply={() => setOpenReplyFor(f.id)}
                            onCloseReply={() => setOpenReplyFor(null)}
                            onReplied={() => { setOpenReplyFor(null); load(); }}
                        />
                    ))}
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, padding: "12px 0 4px", fontSize: 13, color: "var(--bm-text-muted)" }}>
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-feedback-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: "4px 8px" }}><ChevronLeft size={14} /></button>
                        Page {page} of {pages}
                        <button className="bm-btn bm-btn-ghost" data-testid="admin-feedback-next" disabled={page >= pages} onClick={() => setPage(page + 1)} style={{ padding: "4px 8px" }}><ChevronRight size={14} /></button>
                    </div>
                </>
            )}
        </section>
    );
}

// One row of the feedback list — header + message + prior replies + reply composer.
function FeedbackItem({ f, canReply, isOpen, onOpenReply, onCloseReply, onReplied }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const replies = f.admin_replies || [];
    const hasReplied = replies.length > 0;

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed || sending) return;
        setSending(true);
        setError("");
        try {
            await api.post(`/admin/feedback/${f.id}/reply`, { reply: trimmed });
            setText("");
            onReplied();
        } catch (e) {
            setError(formatApiErrorDetail(e) || "Reply failed. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="admin-feedback-item" style={{ padding: "11px 4px", borderBottom: "1px solid var(--bm-border-soft)" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                <span className="bm-tag" style={{ fontSize: 11 }}>{f.category || "general"}</span>
                <span style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                    {f.submitted_email || f.user_id || "anonymous"} · {day(f.created_at)}
                    {f.platform ? ` · ${f.platform}` : ""}{f.page ? ` · ${f.page}` : ""}
                </span>
                {hasReplied && (
                    <span data-testid="admin-feedback-replied-badge" style={{ fontSize: 10, color: "var(--bm-good, #4ade80)", display: "inline-flex", alignItems: "center", gap: 3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        <Check size={11} /> Replied
                    </span>
                )}
            </div>
            <div style={{ fontSize: 14, color: "var(--bm-text)", whiteSpace: "pre-wrap" }}>{f.message}</div>

            {/* Prior replies rendered as a nested thread */}
            {replies.map((r, i) => (
                <div key={i} data-testid="admin-feedback-reply" style={{
                    margin: "10px 0 4px 20px", padding: "8px 12px",
                    borderLeft: "3px solid var(--bm-gold)", background: "rgba(212, 181, 114, 0.06)",
                    borderRadius: "0 6px 6px 0",
                }}>
                    <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                        <CornerDownRight size={11} />
                        <span>{r.by_name || r.by_email || "The Bosun"} · {day(r.at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--bm-text)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{r.text}</div>
                </div>
            ))}

            {/* Reply composer — toggled per row */}
            {isOpen ? (
                <div style={{ marginTop: 10, marginLeft: 20 }}>
                    <textarea
                        data-testid={`admin-feedback-reply-input-${f.id}`}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="A quick note back to the captain…"
                        rows={3}
                        maxLength={4000}
                        autoFocus
                        style={{
                            width: "100%", padding: "8px 10px", fontSize: 13,
                            background: "var(--bm-input-bg)", border: "1px solid var(--bm-border)",
                            borderRadius: 6, color: "var(--bm-text)", resize: "vertical",
                            fontFamily: "inherit", lineHeight: 1.5,
                        }}
                    />
                    {error && (
                        <div style={{ fontSize: 12, color: "var(--bm-warn, #f6a561)", marginTop: 4 }}>{error}</div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                        <button
                            data-testid={`admin-feedback-send-${f.id}`}
                            className="bm-btn bm-btn-gold"
                            onClick={send}
                            disabled={sending || !text.trim()}
                            style={{ padding: "6px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                        >
                            {sending ? <Loader2 size={13} className="bm-spin" /> : <Send size={13} />}
                            Send
                        </button>
                        <button
                            data-testid={`admin-feedback-cancel-${f.id}`}
                            className="bm-btn bm-btn-ghost"
                            onClick={() => { setText(""); setError(""); onCloseReply(); }}
                            disabled={sending}
                            style={{ padding: "6px 12px", fontSize: 13 }}
                        >
                            Cancel
                        </button>
                        <span style={{ fontSize: 11, color: "var(--bm-text-muted)", marginLeft: "auto" }}>
                            {text.length}/4000
                        </span>
                    </div>
                </div>
            ) : (
                <div style={{ marginTop: 8 }}>
                    <button
                        data-testid={`admin-feedback-reply-btn-${f.id}`}
                        className="bm-btn bm-btn-ghost"
                        onClick={onOpenReply}
                        disabled={!canReply}
                        title={canReply ? "Send a personal reply" : "No email on file — can't reply"}
                        style={{
                            padding: "4px 12px", fontSize: 12, display: "inline-flex",
                            alignItems: "center", gap: 6,
                            opacity: canReply ? 1 : 0.5,
                        }}
                    >
                        <CornerDownRight size={12} />
                        {hasReplied ? "Reply again" : "Reply"}
                    </button>
                </div>
            )}
        </div>
    );
}

/* ---------------- Paywall hits (402 instrumentation) ---------------- */
function PaywallHits() {
    const [data, setData] = useState(null);
    const [range, setRange] = useState("30d");
    useEffect(() => {
        api.get("/admin/gate-hits").then(({ data }) => setData(data)).catch(() => {});
    }, []);
    if (!data) return null;
    const rows = range === "30d" ? data.by_feature_30d : data.by_feature_90d;
    return (
        <section data-testid="admin-paywall-section" className="bm-card" style={{ padding: 22, marginTop: 22 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Lock size={16} color="var(--bm-gold)" />
                    <h2 className="bm-serif" style={{ margin: 0, fontSize: 19, fontWeight: 500 }}>Paywall hits</h2>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                    {["30d", "90d"].map((r) => (
                        <button key={r} data-testid={`paywall-range-${r}`} className="bm-btn bm-btn-ghost" onClick={() => setRange(r)}
                            style={{ padding: "4px 12px", fontSize: 12, borderColor: range === r ? "var(--bm-gold)" : undefined, color: range === r ? "var(--bm-gold)" : undefined }}>
                            {r}
                        </button>
                    ))}
                </div>
            </div>
            <div data-testid="paywall-summary" style={{ fontSize: 13, color: "var(--bm-text-muted)", marginBottom: 14 }}>
                <strong style={{ color: "var(--bm-text)" }}>{data.pct_free_users_hit_30d}%</strong> of free captains hit a paywall in the last 30 days
                ({data.unique_users_30d} of {data.free_users}). If this stays under a few percent, the gates are in the wrong place.
            </div>
            {rows.length === 0 ? (
                <div data-testid="paywall-empty" style={{ color: "var(--bm-text-dim)", fontSize: 13, padding: "10px 0" }}>
                    No paywall hits recorded {range === "30d" ? "in the last 30 days" : "in the last 90 days"} yet.
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ textAlign: "left", color: "var(--bm-text-dim)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                <th style={{ padding: "6px 8px" }}>Feature</th>
                                <th style={{ padding: "6px 8px", textAlign: "right" }}>Hits</th>
                                <th style={{ padding: "6px 8px", textAlign: "right" }}>Unique captains</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.feature} data-testid={`paywall-row-${r.feature}`} style={{ borderTop: "1px solid var(--bm-border-soft)" }}>
                                    <td style={{ padding: "8px" }}>{r.feature}</td>
                                    <td style={{ padding: "8px", textAlign: "right" }}>{r.hits}</td>
                                    <td style={{ padding: "8px", textAlign: "right" }}>{r.unique_users}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

/* ---------------- Release switches (Oct 2026 rollout) ---------------- */
function ReleaseSwitches() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(null);
    const [notice, setNotice] = useState("");
    useEffect(() => {
        api.get("/admin/flags").then(({ data }) => setData(data)).catch(() => {});
    }, []);
    if (!data) return null;
    const toggle = async (key) => {
        const next = !data.flags[key];
        setSaving(key);
        try {
            const { data: fresh } = await api.patch("/admin/flags", { [key]: next });
            setData((d) => ({ ...d, flags: fresh.flags, archive_gate_enabled_at: fresh.archive_gate_enabled_at }));
            setNotice(`${key} → ${next ? "ON" : "OFF"}`);
        } catch (e) {
            setNotice(`Failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setSaving(null);
            setTimeout(() => setNotice(""), 3000);
        }
    };
    return (
        <section data-testid="admin-release-switches" className="bm-card" style={{ padding: 22, marginTop: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <SlidersHorizontal size={16} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ margin: 0, fontSize: 19, fontWeight: 500 }}>Release switches</h2>
            </div>
            <p style={{ fontSize: 12.5, color: "var(--bm-text-dim)", margin: "0 0 14px" }}>
                October release controls. Everything ships dark — flip a switch to take that change live for all users, instantly. Flip back to roll back.
            </p>
            {notice && <div data-testid="switches-notice" style={{ fontSize: 12, color: "var(--bm-gold)", marginBottom: 10 }}>{notice}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {Object.keys(data.flags).map((key) => (
                    <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "10px 12px", border: "1px solid var(--bm-border-soft)", borderRadius: 10 }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{key}</div>
                            <div style={{ fontSize: 12, color: "var(--bm-text-muted)", lineHeight: 1.45 }}>{data.labels?.[key]}</div>
                            {key === "archive_gate" && data.archive_gate_enabled_at && (
                                <div style={{ fontSize: 11, color: "var(--bm-text-dim)", marginTop: 3 }}>
                                    Grandfather anchor: entries logged before {data.archive_gate_enabled_at.slice(0, 10)} are always visible.
                                </div>
                            )}
                        </div>
                        <button
                            data-testid={`flag-toggle-${key}`}
                            onClick={() => toggle(key)}
                            disabled={saving === key}
                            aria-pressed={data.flags[key]}
                            aria-label={`Toggle ${key} ${data.flags[key] ? "off" : "on"}`}
                            style={{
                                flexShrink: 0, width: 52, height: 28, borderRadius: 999, position: "relative",
                                border: "1px solid " + (data.flags[key] ? "var(--bm-gold)" : "var(--bm-border)"),
                                background: data.flags[key] ? "rgba(212,181,114,0.25)" : "rgba(255,255,255,0.04)",
                                cursor: saving === key ? "wait" : "pointer",
                                transition: "background-color 160ms ease, border-color 160ms ease",
                            }}
                        >
                            <span style={{
                                position: "absolute", top: 3, left: data.flags[key] ? 26 : 3,
                                width: 20, height: 20, borderRadius: "50%",
                                background: data.flags[key] ? "var(--bm-gold)" : "var(--bm-text-dim)",
                                transition: "left 160ms ease, background-color 160ms ease",
                            }} />
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
}
